import { _ as _async_to_generator } from "@swc/helpers/_/_async_to_generator";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _ts_generator } from "@swc/helpers/_/_ts_generator";
import deepEqual from "deep-equal";
import * as React from "react";
import parseErrorStack from "./parseErrorStack";
export default function useDevToolsBase(ref, callback) {
    var lastStateRef = React.useRef();
    var lastActionRef = React.useRef();
    var callbackRef = React.useRef(callback);
    var lastResetRef = React.useRef(undefined);
    React.useEffect(function() {
        callbackRef.current = callback;
    });
    var symbolicate = function() {
        var _ref = _async_to_generator(function(stack) {
            var _frames__file, _frames_, frames, urlMatch, result, err;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        if (stack == null) {
                            return [
                                2,
                                undefined
                            ];
                        }
                        frames = parseErrorStack(stack).slice(2).filter(function(frame) {
                            return frame.file !== "[native code]";
                        });
                        urlMatch = (_frames_ = frames[0]) === null || _frames_ === void 0 ? void 0 : (_frames__file = _frames_.file) === null || _frames__file === void 0 ? void 0 : _frames__file.match(/^https?:\/\/.+(:\d+)?\//);
                        if (!urlMatch) {
                            return [
                                2,
                                stack
                            ];
                        }
                        _state.label = 1;
                    case 1:
                        _state.trys.push([
                            1,
                            3,
                            ,
                            4
                        ]);
                        return [
                            4,
                            fetch("".concat(urlMatch[0], "symbolicate"), {
                                method: "POST",
                                body: JSON.stringify({
                                    stack: frames
                                })
                            }).then(function(res) {
                                return res.json();
                            })
                        ];
                    case 2:
                        result = _state.sent();
                        return [
                            2,
                            result.stack.filter(function(it) {
                                return !it.collapse;
                            }).map(function(param) {
                                var methodName = param.methodName, file = param.file, lineNumber = param.lineNumber, column = param.column;
                                return "".concat(methodName, "@").concat(file, ":").concat(lineNumber, ":").concat(column);
                            }).join("\n")
                        ];
                    case 3:
                        err = _state.sent();
                        return [
                            2,
                            stack
                        ];
                    case 4:
                        return [
                            2
                        ];
                }
            });
        });
        return function symbolicate(stack) {
            return _ref.apply(this, arguments);
        };
    }();
    var pendingPromiseRef = React.useRef(Promise.resolve());
    var send = React.useCallback(function(data) {
        // We need to make sure that our callbacks executed in the same order
        // So we add check if the last promise is settled before sending the next one
        pendingPromiseRef.current = pendingPromiseRef.current.catch(function() {
        // Ignore any errors from the last promise
        }).then(/*#__PURE__*/ _async_to_generator(function() {
            var stack, err;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        if (!data.stack) return [
                            3,
                            5
                        ];
                        _state.label = 1;
                    case 1:
                        _state.trys.push([
                            1,
                            3,
                            ,
                            4
                        ]);
                        return [
                            4,
                            symbolicate(data.stack)
                        ];
                    case 2:
                        stack = _state.sent();
                        return [
                            3,
                            4
                        ];
                    case 3:
                        err = _state.sent();
                        return [
                            3,
                            4
                        ];
                    case 4:
                        callbackRef.current(_object_spread_props(_object_spread({}, data), {
                            stack: stack
                        }));
                        return [
                            3,
                            6
                        ];
                    case 5:
                        callbackRef.current(data);
                        _state.label = 6;
                    case 6:
                        return [
                            2
                        ];
                }
            });
        }));
    }, []);
    React.useEffect(function() {
        var timer;
        var unsubscribeAction;
        var unsubscribeState;
        var initialize = function() {
            var _ref = _async_to_generator(function() {
                var navigation;
                return _ts_generator(this, function(_state) {
                    switch(_state.label){
                        case 0:
                            if (!!ref.current) return [
                                3,
                                2
                            ];
                            // If the navigation object isn't ready yet, wait for it
                            return [
                                4,
                                new Promise(function(resolve) {
                                    timer = setInterval(function() {
                                        if (ref.current) {
                                            resolve();
                                            clearTimeout(timer);
                                            var state = ref.current.getRootState();
                                            lastStateRef.current = state;
                                            callbackRef.current({
                                                type: "init",
                                                state: state
                                            });
                                        }
                                    }, 100);
                                })
                            ];
                        case 1:
                            _state.sent();
                            _state.label = 2;
                        case 2:
                            navigation = ref.current;
                            unsubscribeAction = navigation.addListener("__unsafe_action__", function(e) {
                                var action = e.data.action;
                                if (e.data.noop) {
                                    // Even if the state didn't change, it's useful to show the action
                                    send({
                                        type: "action",
                                        action: action,
                                        state: lastStateRef.current,
                                        stack: e.data.stack
                                    });
                                } else {
                                    lastActionRef.current = e.data;
                                }
                            });
                            unsubscribeState = navigation.addListener("state", function(e) {
                                // Don't show the action in dev tools if the state is what we sent to reset earlier
                                if (lastResetRef.current && deepEqual(lastResetRef.current, e.data.state)) {
                                    lastStateRef.current = undefined;
                                    return;
                                }
                                var state = navigation.getRootState();
                                var lastState = lastStateRef.current;
                                var lastChange = lastActionRef.current;
                                lastActionRef.current = undefined;
                                lastStateRef.current = state;
                                // If we don't have an action and the state didn't change, then it's probably extraneous
                                if (lastChange === undefined && deepEqual(state, lastState)) {
                                    return;
                                }
                                send({
                                    type: "action",
                                    action: lastChange ? lastChange.action : {
                                        type: "@@UNKNOWN"
                                    },
                                    state: state,
                                    stack: lastChange === null || lastChange === void 0 ? void 0 : lastChange.stack
                                });
                            });
                            return [
                                2
                            ];
                    }
                });
            });
            return function initialize() {
                return _ref.apply(this, arguments);
            };
        }();
        initialize();
        return function() {
            unsubscribeAction === null || unsubscribeAction === void 0 ? void 0 : unsubscribeAction();
            unsubscribeState === null || unsubscribeState === void 0 ? void 0 : unsubscribeState();
            clearTimeout(timer);
        };
    }, [
        ref,
        send
    ]);
    var resetRoot = React.useCallback(function(state) {
        if (ref.current) {
            lastResetRef.current = state;
            ref.current.resetRoot(state);
        }
    }, [
        ref
    ]);
    return {
        resetRoot: resetRoot
    };
}
